import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';
import { formatCurrency } from '@src/scripts/util/currency';

const WIDTH = 600;
const HEIGHT = 260;
const PADDING_TOP = 16;
const PADDING_RIGHT = 16;
const PADDING_BOTTOM = 28;
const PADDING_LEFT = 60;
const Y_TICK_COUNT = 4;
const MONTH_TICK_STEP = 6;

// Compact axis labels (e.g. "€34K") — formatCurrency() from util/currency
// is for full precision display values, not axis ticks.
const formatCompact = ( value, currency ) =>
	new Intl.NumberFormat( undefined, {
		style: 'currency',
		currency,
		notation: 'compact',
		maximumFractionDigits: 0,
	} ).format( value );

// Mirrors the reference tool's "Amortisationskurve" (Praxis) /
// "Kumulierter Cashflow" (Labor) chart: a cumulative line starting at
// month 0, crossing zero at the breakeven month for Praxis (Labor is
// financed via the lease itself, so it has no upfront deficit to cross),
// a tinted area fill, and (confirmed live on the reference tool) a hover
// crosshair + tooltip reporting the exact value at whichever month the
// cursor is over. Plain SVG rather than a charting library.
//
// Stretches to fill whatever height &__chart-card's flex column gives it
// (see style.scss) via `preserveAspectRatio="none"` on the <svg> — a
// straight line, a filled area, and gridlines all stretch without looking
// "wrong" (there's no correct angle to preserve: X is months, Y is
// currency, unrelated units to begin with), as long as two things are
// handled explicitly:
// - Stroked lines use `vectorEffect="non-scaling-stroke"`, or the stroke
//   width itself would stretch non-uniformly right along with the
//   coordinate system, making straight lines/dashes look uneven.
// - All *text* (axis labels, tooltip, "ROI Nm") and the breakeven/hover
//   *dot* markers render as plain HTML overlays, positioned by percentage
//   over the chart, rather than inside the SVG — glyphs and circles both
//   visibly distort under non-uniform scaling (squished/stretched letters;
//   an ellipse instead of a circle) in a way straight lines don't.
const CumulativeChart = ( { data, breakevenMonth, currency, locale } ) => {
	const containerRef = useRef( null );
	const [ hoveredMonth, setHoveredMonth ] = useState( null );

	if ( ! data || data.length === 0 ) {
		return null;
	}

	const months = data.map( ( point ) => point.month );
	const values = data.map( ( point ) => point.cumulative );
	const minValue = Math.min( 0, ...values );
	const maxValue = Math.max( 0, ...values );
	const valueRange = maxValue - minValue || 1;
	const maxMonth = Math.max( ...months ) || 1;

	const x = ( month ) =>
		PADDING_LEFT +
		( month / maxMonth ) * ( WIDTH - PADDING_LEFT - PADDING_RIGHT );
	const y = ( value ) =>
		HEIGHT -
		PADDING_BOTTOM -
		( ( value - minValue ) / valueRange ) *
			( HEIGHT - PADDING_TOP - PADDING_BOTTOM );

	const linePath = data
		.map(
			( point, index ) =>
				`${ index === 0 ? 'M' : 'L' } ${ x( point.month ) } ${ y(
					point.cumulative
				) }`
		)
		.join( ' ' );

	const zeroY = y( 0 );
	// Filled band between the curve and the zero baseline — shows the
	// deficit/profit region without needing a second series or color.
	const areaPath = `M ${ x( 0 ) } ${ zeroY } ${ data
		.map(
			( point ) => `L ${ x( point.month ) } ${ y( point.cumulative ) }`
		)
		.join( ' ' ) } L ${ x( maxMonth ) } ${ zeroY } Z`;

	const hasBreakeven =
		Number.isFinite( breakevenMonth ) && breakevenMonth <= maxMonth;

	const yTicks = Array.from(
		{ length: Y_TICK_COUNT + 1 },
		( _, index ) => minValue + ( valueRange * index ) / Y_TICK_COUNT
	);
	const xTicks = [];
	for ( let month = 0; month <= maxMonth; month += MONTH_TICK_STEP ) {
		xTicks.push( month );
	}

	// The breakeven month is Math.ceil(price / monthlySavings) — rounded UP
	// to the nearest whole month (see roiMath.js), so the curve's actual
	// value *at* that month is virtually never exactly 0; it's already
	// past breakeven by however much that month's savings overshot the
	// remaining balance. Pinning the dot to y(0) (the zero baseline) put
	// it on the dashed crosshair intersection but off the actual line —
	// visibly drifting away from it by that overshoot amount, worse for
	// some inputs than others. Reading the real data point at that month
	// keeps the dot glued to the curve, where it belongs.
	const breakevenPoint = hasBreakeven
		? data.find( ( point ) => point.month === breakevenMonth )
		: null;
	const dotLeft = hasBreakeven ? ( x( breakevenMonth ) / WIDTH ) * 100 : 0;
	const dotTop = breakevenPoint
		? ( y( breakevenPoint.cumulative ) / HEIGHT ) * 100
		: 0;
	const labelAtEdge = hasBreakeven && breakevenMonth / maxMonth > 0.85;

	const handleMouseMove = ( event ) => {
		if ( ! containerRef.current ) {
			return;
		}

		const rect = containerRef.current.getBoundingClientRect();
		const relativeX = ( event.clientX - rect.left ) / rect.width;
		const month = Math.round(
			Math.max( 0, Math.min( 1, relativeX ) ) * maxMonth
		);

		setHoveredMonth( month );
	};

	const handleMouseLeave = () => setHoveredMonth( null );

	const hoveredPoint =
		hoveredMonth !== null
			? data.find( ( point ) => point.month === hoveredMonth )
			: null;
	const hoveredLeft = hoveredPoint
		? ( x( hoveredPoint.month ) / WIDTH ) * 100
		: 0;
	const hoveredTop = hoveredPoint
		? ( y( hoveredPoint.cumulative ) / HEIGHT ) * 100
		: 0;
	const hoveredAtEdge = hoveredPoint && hoveredPoint.month / maxMonth > 0.85;

	return (
		<div
			className="roi-calculator__line-chart-container"
			ref={ containerRef }
			onMouseMove={ handleMouseMove }
			onMouseLeave={ handleMouseLeave }
		>
			<svg
				className="roi-calculator__line-chart"
				viewBox={ `0 0 ${ WIDTH } ${ HEIGHT }` }
				preserveAspectRatio="none"
				role="img"
				aria-label={ __(
					'Cumulative cashflow over time',
					'sprintray-roi-calculator'
				) }
			>
				{ yTicks.map( ( tickValue ) => (
					<line
						key={ tickValue }
						x1={ PADDING_LEFT }
						x2={ WIDTH - PADDING_RIGHT }
						y1={ y( tickValue ) }
						y2={ y( tickValue ) }
						vectorEffect="non-scaling-stroke"
						className="roi-calculator__line-chart-grid"
					/>
				) ) }

				<path
					d={ areaPath }
					className="roi-calculator__line-chart-area"
					stroke="none"
				/>

				<line
					x1={ PADDING_LEFT }
					x2={ WIDTH - PADDING_RIGHT }
					y1={ zeroY }
					y2={ zeroY }
					vectorEffect="non-scaling-stroke"
					className="roi-calculator__line-chart-zero"
				/>

				{ hasBreakeven && (
					<line
						x1={ x( breakevenMonth ) }
						x2={ x( breakevenMonth ) }
						y1={ PADDING_TOP }
						y2={ HEIGHT - PADDING_BOTTOM }
						vectorEffect="non-scaling-stroke"
						className="roi-calculator__line-chart-breakeven"
					/>
				) }

				{ hoveredPoint && (
					<line
						x1={ x( hoveredPoint.month ) }
						x2={ x( hoveredPoint.month ) }
						y1={ PADDING_TOP }
						y2={ HEIGHT - PADDING_BOTTOM }
						vectorEffect="non-scaling-stroke"
						className="roi-calculator__line-chart-hover-line"
					/>
				) }

				<path
					d={ linePath }
					className="roi-calculator__line-chart-path"
					fill="none"
					vectorEffect="non-scaling-stroke"
				/>
			</svg>

			{ yTicks.map( ( tickValue ) => (
				<span
					key={ tickValue }
					className="roi-calculator__line-chart-y-label"
					style={ { top: `${ ( y( tickValue ) / HEIGHT ) * 100 }%` } }
				>
					{ formatCompact( tickValue, currency ) }
				</span>
			) ) }

			{ xTicks.map( ( month ) => (
				<span
					key={ month }
					className="roi-calculator__line-chart-x-label"
					style={ { left: `${ ( x( month ) / WIDTH ) * 100 }%` } }
				>
					{ month }M
				</span>
			) ) }

			{ hasBreakeven && (
				<div
					className="roi-calculator__line-chart-dot"
					style={ { left: `${ dotLeft }%`, top: `${ dotTop }%` } }
				/>
			) }

			{ hasBreakeven && (
				<span
					className={
						'roi-calculator__line-chart-roi-label' +
						( labelAtEdge
							? ' roi-calculator__line-chart-roi-label--edge'
							: '' )
					}
					style={ { left: `${ dotLeft }%`, top: `${ dotTop }%` } }
				>
					{ sprintf(
						/* translators: %d: number of months until return on investment */
						__( 'ROI %dm', 'sprintray-roi-calculator' ),
						breakevenMonth
					) }
				</span>
			) }

			{ hoveredPoint && (
				<>
					<div
						className="roi-calculator__line-chart-dot roi-calculator__line-chart-dot--hover"
						style={ {
							left: `${ hoveredLeft }%`,
							top: `${ hoveredTop }%`,
						} }
					/>
					<div
						className={
							'roi-calculator__line-chart-tooltip' +
							( hoveredAtEdge
								? ' roi-calculator__line-chart-tooltip--edge'
								: '' )
						}
						style={ {
							left: `${ hoveredLeft }%`,
							top: `${ hoveredTop }%`,
						} }
					>
						<strong className="roi-calculator__line-chart-tooltip-title">
							{ sprintf(
								/* translators: %d: month number */
								__( 'Month %d', 'sprintray-roi-calculator' ),
								hoveredPoint.month
							) }
						</strong>
						<div className="roi-calculator__line-chart-tooltip-row">
							{ formatCurrency(
								hoveredPoint.cumulative,
								currency,
								locale
							) }
						</div>
					</div>
				</>
			) }
		</div>
	);
};

CumulativeChart.propTypes = {
	data: PropTypes.arrayOf(
		PropTypes.shape( {
			month: PropTypes.number.isRequired,
			cumulative: PropTypes.number.isRequired,
		} )
	).isRequired,
	breakevenMonth: PropTypes.number,
	currency: PropTypes.string,
	locale: PropTypes.string,
};

export default CumulativeChart;
